import { api } from './base';

import { BoxDistributionType } from 'v2/constants';
import { MutationArg, Pageable } from 'v2/types';
import { SlotModel } from 'v2/types/SlotModel';
import { SWRKeyPagination, SWRKeyPaginationWithExtra, SWRKeyValue } from 'v2/types/swr';
import { PayoutBalanceForm, TournamentFilter, TournamentForm, TournamentModel } from 'v2/types/TournamentModel';
import { exportFile } from 'v2/utils';

export const tournamentsService = {
  getProviderSlotsByTournamentId([_key, tournamentId]: SWRKeyValue): Promise<SlotModel[]> {
    return api.get(`v1/admin/provider-slot/tournament/${tournamentId}`).json();
  },

  getTournaments([_key, page, limit, filter]: SWRKeyPaginationWithExtra<TournamentFilter>): Promise<
    Pageable<TournamentModel>
  > {
    return api
      .get('admin/tournament', {
        searchParams: {
          page,
          limit,
          filter: JSON.stringify(filter),
          sortFields: JSON.stringify({ startDate: 'asc' }),
        },
      })
      .json();
  },

  getAllTournaments([_key]: SWRKeyPagination): Promise<TournamentModel[]> {
    return api.get('admin/tournament/all').json();
  },

  getActiveTournament(): Promise<TournamentModel> {
    return api.get('admin/tournament/active').json();
  },

  createTournament(_key: string, { arg }: MutationArg<TournamentForm>): Promise<TournamentModel> {
    return api.post('admin/tournament', { json: arg }).json();
  },

  updateTournament([_key, tournamentId]: SWRKeyValue, { arg }: MutationArg<TournamentForm>): Promise<TournamentModel> {
    return api.put(`admin/tournament/${tournamentId}`, { json: arg }).json();
  },

  setActiveTournament(
    [_key, tournamentId]: SWRKeyValue,
    { arg: isActive }: MutationArg<boolean>
  ): Promise<TournamentModel> {
    return api.patch(`admin/tournament/${tournamentId}/active`, { json: { isActive } }).json();
  },

  markTournamentAsDeleted([_key, tournamentId]: SWRKeyValue): Promise<void> {
    return api.delete(`admin/tournament/${tournamentId}`).json();
  },

  updateUsersTournamentBalance(
    [_key, tournamentId]: SWRKeyValue,
    { arg }: MutationArg<PayoutBalanceForm>
  ): Promise<void> {
    return api.patch(`admin/balance/tournament/${tournamentId}/all`, { json: arg }).json();
  },

  updateUsersTournamentBalanceByLevel(
    [_key, tournamentId]: SWRKeyValue,
    { arg }: MutationArg<PayoutBalanceForm>
  ): Promise<void> {
    return api.patch(`admin/balance/tournament/${tournamentId}/level-bonus`, { json: arg }).json();
  },

  getTournamentsHistory([_key, page, limit, filter]: SWRKeyPaginationWithExtra<TournamentFilter>): Promise<
    Pageable<TournamentModel>
  > {
    return api
      .get('admin/tournament', {
        searchParams: {
          page,
          limit,
          filter: JSON.stringify(filter),
          sortFields: JSON.stringify({ endDate: 'desc' }),
        },
      })
      .json();
  },

  sendBoxesToTournamentParticipants(
    [_key, tournamentId]: SWRKeyValue,
    { arg }: MutationArg<{ userCount: number; distributionType: BoxDistributionType }>
  ): Promise<void> {
    return api.patch(`admin/tournament/${tournamentId}/reward`, { json: arg }).json();
  },

  exportTournamentResults(
    [_key, tournamentId]: SWRKeyValue,
    { arg }: MutationArg<{ limit: number; roles: string }>
  ): Promise<void> {
    return api
      .get(`admin/tournament/${tournamentId}/export`, { searchParams: arg })
      .blob()
      .then((blob) => exportFile(blob, 'tournament_results'));
  },

  updateTournamentBalance(
    [_key, userId]: SWRKeyValue,
    { arg }: MutationArg<{ tournamentId: string; balance: number }>
  ): Promise<TournamentModel> {
    return api
      .put(`admin/balance/${userId}/tournament/${arg.tournamentId}/update`, { json: { balance: arg.balance } })
      .json();
  },

  resetTournamentBalance([_key, userId]: SWRKeyValue, { arg: tournamentId }: MutationArg<string>): Promise<void> {
    return api.delete(`admin/balance/${userId}/tournament/${tournamentId}/delete`).json();
  },
};
